// Contexte entreprise — gère l'entreprise sélectionnée dans le sélecteur sidebar
// Persiste la sélection en localStorage pour survivre aux rechargements

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import { useAuth } from './AuthContext'

const STORAGE_KEY = 'autodiag:entreprise-courante'

interface SsoCompany {
  id: number
  name: string
  siret?: string
  numEservice?: string
  employeeCount?: number
}

interface EntrepriseContextType {
  /** Liste des organisations de l'utilisateur (depuis SSO) */
  organisations: string[]
  /** Organisation actuellement sélectionnée */
  currentEntreprise: string | null
  /** Objet SSO complet de l'entreprise courante (siret, effectif, etc.) */
  currentCompany: SsoCompany | null
  /** Changer l'organisation courante */
  setCurrentEntreprise: (nom: string) => void
  /** num_eservice de l'entreprise courante (pour GTM) */
  currentNumEservice: string | null
  /** SIRET effectif pour l'entreprise courante (SSO en priorité, sinon valeur saisie localement). */
  currentSiret: string | null
  /** Indique si la source du SIRET est le SSO (lecture seule) ou local (modifiable). */
  currentSiretSource: 'sso' | 'local' | null
}

const EntrepriseContext = createContext<EntrepriseContextType | null>(null)

export function EntrepriseProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const organisations = user?.organisations ?? []

  const [currentEntreprise, setCurrentEntrepriseState] = useState<string | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored || null
  })

  // Synchroniser avec les organisations disponibles
  useEffect(() => {
    if (organisations.length === 0) return

    // Si la sélection stockée n'est pas dans la liste, prendre la première
    if (!currentEntreprise || !organisations.includes(currentEntreprise)) {
      setCurrentEntrepriseState(organisations[0])
      localStorage.setItem(STORAGE_KEY, organisations[0])
    }
  }, [organisations, currentEntreprise])

  const setCurrentEntreprise = useCallback((nom: string) => {
    setCurrentEntrepriseState(nom)
    localStorage.setItem(STORAGE_KEY, nom)
  }, [])

  const currentCompany = useMemo<SsoCompany | null>(() => {
    if (!currentEntreprise || !user?.ssoCompanies) return null
    return (user.ssoCompanies.find(c => c.name === currentEntreprise) as SsoCompany | undefined) ?? null
  }, [currentEntreprise, user?.ssoCompanies])

  const currentNumEservice = currentCompany?.numEservice ?? null

  // SIRET résolu pour l'org courante : SSO d'abord, sinon valeur saisie localement (siretByOrg[idOrganization]).
  const { currentSiret, currentSiretSource } = useMemo<{
    currentSiret: string | null
    currentSiretSource: 'sso' | 'local' | null
  }>(() => {
    if (!currentCompany) return { currentSiret: null, currentSiretSource: null }
    if (currentCompany.siret) {
      return { currentSiret: currentCompany.siret, currentSiretSource: 'sso' }
    }
    const local = user?.siretByOrg?.[String(currentCompany.id)] || null
    return { currentSiret: local, currentSiretSource: local ? 'local' : null }
  }, [currentCompany, user?.siretByOrg])

  return (
    <EntrepriseContext.Provider
      value={{
        organisations,
        currentEntreprise,
        currentCompany,
        setCurrentEntreprise,
        currentNumEservice,
        currentSiret,
        currentSiretSource,
      }}
    >
      {children}
    </EntrepriseContext.Provider>
  )
}

export function useEntreprise() {
  const context = useContext(EntrepriseContext)
  if (!context) {
    throw new Error('useEntreprise must be used within an EntrepriseProvider')
  }
  return context
}
